import { FC, useCallback, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { useData } from "../context/DataContext";
import { listDynamicLinks, revokeDynamicLink } from "../services/api";
import type { DynamicLinkDTO } from "../types";
import { formatDate } from "../utils/utils";
import GenerateDynamicLinksModal from "../components/GenerateDynamicLinksModal";

// URL cliccabile in-app: usa l'origine corrente (dev: localhost, prod: qrflow.it).
const publicUrl = (publicId: string) => `${window.location.origin}/d/${publicId}`;

const DynamicLinksPage: FC = () => {
    const { clients } = useData();

    const [links, setLinks] = useState<DynamicLinkDTO[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeClient, setActiveClient] = useState<number>(-1);
    const [typeFilter, setTypeFilter] = useState<"all" | "qr" | "oneshot">("all");
    const [statusFilter, setStatusFilter] = useState<"active" | "revoked" | "expired" | "all">(
        "active",
    );
    const [revokingId, setRevokingId] = useState<number | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const matchesType = (l: DynamicLinkDTO) =>
        typeFilter === "all" ? true : typeFilter === "oneshot" ? !!l.oneShot : !l.oneShot;
    const matchesStatus = (l: DynamicLinkDTO) => {
        switch (statusFilter) {
            case "active":
                return !l.revoked && !l.expired;
            case "revoked":
                return l.revoked;
            case "expired":
                return !l.revoked && !!l.expired;
            default:
                return true;
        }
    };
    const filteredLinks = links.filter((l) => matchesType(l) && matchesStatus(l));

    const fetchLinks = useCallback(async (clientId: number) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await listDynamicLinks(clientId);
            setLinks(response.data.data);
        } catch (err: any) {
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Impossibile caricare i link dinamici.",
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLinks(activeClient);
    }, [activeClient, fetchLinks]);

    const handleRevoke = async (id: number) => {
        if (!window.confirm("Revocare questo link? L'operazione non è reversibile.")) return;
        setRevokingId(id);
        try {
            await revokeDynamicLink(id);
            setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, revoked: true } : l)));
        } catch (err: any) {
            alert(err?.response?.data?.message || err?.message || "Errore durante la revoca.");
        } finally {
            setRevokingId(null);
        }
    };

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", filename);
        document.body.appendChild(a);
        a.click();
        a.parentNode?.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    // L'export segue i filtri attivi (cliente/tipo/stato) ed è generato client-side dalle
    // righe visualizzate. L'URL nel file resta quello di PRODUZIONE (l.url), da distribuire
    // agli utenti finali; solo i link cliccabili in-app usano l'origine corrente.
    const handleExport = async (format: "csv" | "xlsx") => {
        setIsExporting(true);
        try {
            const rows = filteredLinks.map((l) => ({
                descrizione: l.label || l.code || "",
                tipo: l.oneShot
                    ? `Oneshot${l.oneShotGroupName ? " - " + l.oneShotGroupName : ""}`
                    : "QR singolo",
                code: l.code ?? "",
                url: l.url,
                accessi:
                    l.maxAccesses != null && l.maxAccesses !== -1
                        ? `${l.accessCount} / ${l.maxAccesses}`
                        : String(l.accessCount),
                scadenza: l.validTo ? formatDate(l.validTo) : "",
                stato: l.revoked ? "Revocato" : l.expired ? "Scaduto" : "Attivo",
            }));
            const header = [
                "Descrizione",
                "Tipo",
                "Code QR",
                "URL pubblico",
                "Accessi",
                "Scadenza",
                "Stato",
            ];
            if (format === "csv") {
                const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
                const lines = [
                    header.map(escape).join(","),
                    ...rows.map((r) =>
                        [r.descrizione, r.tipo, r.code, r.url, r.accessi, r.scadenza, r.stato]
                            .map(escape)
                            .join(","),
                    ),
                ];
                const blob = new Blob(["﻿" + lines.join("\r\n")], {
                    type: "text/csv;charset=utf-8;",
                });
                triggerDownload(blob, `link_dinamici_${Date.now()}.csv`);
            } else {
                const workbook = new ExcelJS.Workbook();
                const ws = workbook.addWorksheet("Link Dinamici");
                ws.columns = [
                    { header: "Descrizione", key: "descrizione", width: 30 },
                    { header: "Tipo", key: "tipo", width: 22 },
                    { header: "Code QR", key: "code", width: 28 },
                    { header: "URL pubblico", key: "url", width: 45 },
                    { header: "Accessi", key: "accessi", width: 12 },
                    { header: "Scadenza", key: "scadenza", width: 20 },
                    { header: "Stato", key: "stato", width: 12 },
                ];
                ws.getRow(1).font = { bold: true };
                rows.forEach((r) => ws.addRow(r));
                const buffer = await workbook.xlsx.writeBuffer();
                const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                triggerDownload(blob, `link_dinamici_${Date.now()}.xlsx`);
            }
        } catch (err: any) {
            alert(err?.message || "Errore durante l'export.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Link Dinamici</h1>
                    <p className="text-slate-400">
                        Gestisci i link con QR rotante: consulta, revoca ed esporta l'elenco.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setShowCreateModal(true)}
                        disabled={activeClient === -1}
                        title={
                            activeClient === -1
                                ? "Seleziona prima un cliente per generare i link"
                                : "Crea un link dinamico per tutti i QR del cliente"
                        }
                        className="px-4 py-2 bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white text-sm font-bold rounded-lg shadow-lg shadow-sky-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        ＋ Crea link dinamici
                    </button>
                    <select
                        value={activeClient}
                        onChange={(e) => setActiveClient(Number(e.target.value))}
                        className="bg-slate-700/50 border border-slate-600 px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                        <option value={-1}>Tutti i clienti</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value as "all" | "qr" | "oneshot")}
                        className="bg-slate-700/50 border border-slate-600 px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                        <option value="all">Tutti i tipi</option>
                        <option value="qr">QR singoli</option>
                        <option value="oneshot">Oneshot</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) =>
                            setStatusFilter(e.target.value as "active" | "revoked" | "expired" | "all")
                        }
                        className="bg-slate-700/50 border border-slate-600 px-4 py-2 rounded-lg text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                        <option value="active">Attivi</option>
                        <option value="expired">Scaduti</option>
                        <option value="revoked">Revocati</option>
                        <option value="all">Tutti gli stati</option>
                    </select>
                    <button
                        onClick={() => handleExport("csv")}
                        disabled={isExporting}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                    >
                        Export CSV
                    </button>
                    <button
                        onClick={() => handleExport("xlsx")}
                        disabled={isExporting}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                    >
                        Export XLSX
                    </button>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-red-900/20 border border-red-800/50 text-red-200 rounded-xl">
                    {error}
                </div>
            )}

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-800">
                            <tr>
                                <th className="p-4">Descrizione</th>
                                <th className="p-4 hidden md:table-cell">Tipo</th>
                                <th className="p-4">URL pubblico</th>
                                <th className="p-4 hidden md:table-cell">Rotazione</th>
                                <th className="p-4 hidden lg:table-cell">Accessi</th>
                                <th className="p-4 hidden sm:table-cell">Scadenza</th>
                                <th className="p-4">Stato</th>
                                <th className="p-4 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={8} className="text-center p-8 text-slate-400">
                                        Caricamento...
                                    </td>
                                </tr>
                            ) : filteredLinks.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center p-8 text-slate-400">
                                        Nessun link dinamico trovato.
                                    </td>
                                </tr>
                            ) : (
                                filteredLinks.map((l) => (
                                    <tr key={l.id} className="hover:bg-slate-800 transition-colors">
                                        <td className="p-4 text-white font-medium">
                                            {l.label || l.code}
                                            {l.hasPassword && (
                                                <span className="ml-2 text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 uppercase font-bold">
                                                    Password
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 hidden md:table-cell">
                                            {l.oneShot ? (
                                                <span
                                                    className="text-xs px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 font-semibold"
                                                    title={l.oneShotGroupName || "Oneshot"}
                                                >
                                                    Oneshot
                                                    {l.oneShotGroupName ? ` · ${l.oneShotGroupName}` : ""}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-slate-400">QR singolo</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <a
                                                href={publicUrl(l.publicId)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-sky-400 hover:text-sky-300 font-mono text-sm break-all"
                                            >
                                                {publicUrl(l.publicId)}
                                            </a>
                                        </td>
                                        <td className="p-4 text-slate-300 hidden md:table-cell">
                                            {l.rotationSeconds}s
                                        </td>
                                        <td className="p-4 text-slate-300 hidden lg:table-cell">
                                            {l.accessCount}
                                            {l.maxAccesses != null && l.maxAccesses !== -1
                                                ? ` / ${l.maxAccesses}`
                                                : ""}
                                        </td>
                                        <td className="p-4 text-slate-300 hidden sm:table-cell">
                                            {l.validTo ? formatDate(l.validTo) : "Mai"}
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={`px-2 py-0.5 rounded text-xs uppercase font-bold ${
                                                    l.revoked
                                                        ? "bg-red-500/10 text-red-400"
                                                        : l.expired
                                                            ? "bg-amber-500/10 text-amber-400"
                                                            : "bg-emerald-500/10 text-emerald-400"
                                                }`}
                                            >
                                                {l.revoked ? "Revocato" : l.expired ? "Scaduto" : "Attivo"}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            {!l.revoked && (
                                                <button
                                                    onClick={() => handleRevoke(l.id)}
                                                    disabled={revokingId === l.id}
                                                    className="text-red-400 hover:text-red-300 text-sm font-semibold disabled:opacity-50"
                                                >
                                                    {revokingId === l.id ? "..." : "Revoca"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreateModal && activeClient !== -1 && (
                <GenerateDynamicLinksModal
                    allOfClientId={activeClient}
                    onClose={() => {
                        setShowCreateModal(false);
                        fetchLinks(activeClient);
                    }}
                />
            )}
        </div>
    );
};

export default DynamicLinksPage;
